import { Injectable } from '@angular/core';
import { UploadObj, UploadTimingPart } from '../classes/upload-obj.class';
import { estimatedTimeToCreateAsset, maxConcurrentUploads, multiPartUploadConcurrency } from '../consts';

@Injectable({
  providedIn: 'root',
})
export class UploadStatService {
  private getMostRecentUploads(uploads: UploadObj[]) {
    return uploads
      .filter(upload => upload.status === 'done')
      .sort((a, b) => b.timings.upload_finished_at - a.timings.upload_finished_at)
      .slice(0, 10);
  }
  private getMostRecentDonePartUploads(uploads: UploadObj[]) {
    return uploads
      .filter(upload => upload.status === 'started' && upload.timings.parts)
      .sort((a, b) => b.timings.upload_finished_at - a.timings.upload_finished_at)
      .slice(0, 10);
  }

  public getConcurrency(uploads: UploadObj[]) {
    return Math.min(maxConcurrentUploads, this.getMostRecentUploads(uploads).length) || 1;
  }

  private getUploadSpeedFromDoneUploads(uploads: UploadObj[]) {
    const mostRecentDoneUploads = this.getMostRecentUploads(uploads);
    if (!mostRecentDoneUploads.length) return undefined;
    const size = mostRecentDoneUploads.reduce((t, upload) => t + upload.file.size, 0);
    const timeSpentUploading = mostRecentDoneUploads.reduce(
      (t, upload) => t + upload.timings.upload_finished_at - upload.timings.upload_started_at,
      0
    );
    if (!size || !timeSpentUploading) return 0;
    const uploadSpeedForThisFile = size / timeSpentUploading;
    const concurrency = Math.min(maxConcurrentUploads, mostRecentDoneUploads.length);
    return uploadSpeedForThisFile * concurrency;
  }

  private getUploadSpeedFromDoneUploadParts(uploads: UploadObj[]) {
    const mostRecentDonePartUploads = this.getMostRecentDonePartUploads(uploads);
    if (!mostRecentDonePartUploads.length) return undefined;
    const parts = mostRecentDonePartUploads.reduce(
      (t, upload) => t.concat(upload.timings.parts),
      [] as UploadTimingPart[]
    );
    const completedParts = parts.filter(part => part.finished_at);

    const completedSize = completedParts.reduce((t, part) => t + part.size, 0);
    const timeSpentUploading = completedParts.reduce((t, part) => t + part.finished_at - part.started_at, 0);
    if (!completedSize || !timeSpentUploading) return 0;
    const uploadSpeedForThisFile = completedSize / timeSpentUploading;
    const concurrency = Math.min(multiPartUploadConcurrency, parts.length);

    return uploadSpeedForThisFile * concurrency;
  }

  /**
   * @returns bytes per millisecond
   */
  public getUploadSpeed(uploads: UploadObj[]) {
    const speed = this.getUploadSpeedFromDoneUploads(uploads);
    if (speed) return speed;
    // If the user is uploading their first file, and that file is a multi-part file (e.g. a large video), we calculate the speed using the done parts of the most recent upload
    return this.getUploadSpeedFromDoneUploadParts(uploads);
  }

  public getTotalSize(uploads: UploadObj[]) {
    return uploads.filter(u => u.status !== 'error').reduce((t, v) => t + BigInt(v.file.size), 0n);
  }

  public getRemainingSize(uploads: UploadObj[], uploadSpeed: number) {
    return uploads
      .filter(u => !['done', 'error'].includes(u.status))
      .reduce((t, v) => {
        if (v.timings.upload_started_at && uploadSpeed) {
          // in this case, the asset has already started uploading, so we can calculate the remaining size using the start time and the upload speed
          const timeSpentUploading = Date.now() - v.timings.upload_started_at;

          const concurrency = Math.min(maxConcurrentUploads, this.getConcurrency(uploads));
          const alreadyUploaded = (uploadSpeed * timeSpentUploading) / concurrency;
          const remaining = Math.max(v.file.size - alreadyUploaded, 0);
          return t + BigInt(Math.round(remaining));
        }

        return t + BigInt(v.file.size);
      }, 0n);
  }

  public getRemainingTime(uploads: UploadObj[], uploadSpeed: number) {
    if (!uploadSpeed) return;
    const mostRecentDoneUploads = this.getMostRecentUploads(uploads);

    const remainingKb = Number(this.getRemainingSize(uploads, uploadSpeed) / 1024n);

    const timeSpentCreating =
      mostRecentDoneUploads.reduce(
        (t, upload) => t + upload.timings.asset_creation_finished_at - upload.timings.asset_creation_started_at,
        0
      ) || estimatedTimeToCreateAsset;

    const assetsToBeCreated = uploads.filter(
      u => !['done', 'error'].includes(u.status) && !u.timings.asset_creation_finished_at
    ).length;

    if (!remainingKb || !timeSpentCreating) return 0;

    const timeSpentCreatingOneAsset = timeSpentCreating / (mostRecentDoneUploads.length || 1);
    const timeLeftForUpload = (remainingKb / uploadSpeed) * 1024;
    const timeLeftForCreation = timeSpentCreatingOneAsset * assetsToBeCreated || 0;
    const remainingTime = timeLeftForUpload + timeLeftForCreation;

    if (remainingTime < 0) return 0;
    return Math.floor(remainingTime);
  }
}
